import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  sortableKeyboardCoordinates
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useReorderableList } from "../utils/useReorderableList";

const SortableRow = ({ id, index, item, disabled, renderItem, renderSeparator }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  };

  return (
    <div ref={setNodeRef} style={style} className={isDragging ? "opacity-50" : undefined}>
      {renderItem(item, index, {
        dragHandleProps: { ...attributes, ...listeners },
        isDragging
      })}
      {renderSeparator?.(index)}
    </div>
  );
};

/**
 * Drag-and-drop reorderable list (mouse + touch + keyboard via @dnd-kit).
 * Pass `renderItem` a render prop so each screen keeps its own row markup;
 * spread the returned `dragHandleProps` on whichever element should act as
 * the drag handle.
 */
export const ReorderableList = ({
  items,
  getItemId,
  onChange,
  renderItem,
  renderSeparator,
  disabled = false,
  reindexKey
}) => {
  const { reorder } = useReorderableList(items, onChange, { reindexKey });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const ids = items.map(getItemId);

  const handleDragEnd = event => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    reorder(ids.indexOf(active.id), ids.indexOf(over.id));
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        {items.map((item, index) => (
          <SortableRow
            key={ids[index]}
            id={ids[index]}
            index={index}
            item={item}
            disabled={disabled}
            renderItem={renderItem}
            renderSeparator={renderSeparator}
          />
        ))}
      </SortableContext>
    </DndContext>
  );
};

export default ReorderableList;
